import React, { useRef, useState, useEffect } from 'react';
import { usePhotos } from '../hooks/usePhotos';
import './PhotoUpload.css';

const isPdf = (url) => url?.toLowerCase().endsWith('.pdf') || url?.toLowerCase().includes('pdf');

const PdfIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <path d="M16 13H8"/>
    <path d="M16 17H8"/>
    <path d="M10 9H8"/>
  </svg>
);

const PhotoUpload = ({ jobId, onPhotosSaved, legacyPhotos, jobTitle, jobDate }) => {
  const { photos: savedPhotos, loading, addPhoto, removePhoto: removeSavedPhoto } = usePhotos(jobId);
  const fileInputRef = useRef(null);
  const [queuedFiles, setQueuedFiles] = useState([]);
  const [uploadingAll, setUploadingAll] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Lightbox with index support (like Job Photos)
  const [lightbox, setLightbox] = useState({ isOpen: false, index: 0, url: '', name: '' });
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const lightboxImgRef = useRef(null);
  const isOnline = navigator.onLine;
  const [newPhotoIds, setNewPhotoIds] = useState(new Set());
  const [touchStart, setTouchStart] = useState(null);
  const scrollRef = useRef(null);

  // Detect "new" photos based on last viewed timestamp (works across users/devices)
  useEffect(() => {
    if (!savedPhotos.length || !jobId) return;
    const lastSeenKey = `fieldsync_last_seen_${jobId}`;
    const lastSeen = parseInt(localStorage.getItem(lastSeenKey) || '0', 10);
    const now = Date.now();
    
    // First visit: set timestamp, nothing is "new"
    if (!lastSeen) {
      localStorage.setItem(lastSeenKey, now.toString());
      return;
    }
    
    // Photos uploaded after last visit are "new"
    const newIds = new Set();
    savedPhotos.forEach(p => {
      const uploadedAt = p.uploadedAt?.toMillis?.() 
        || (p.uploadedAt?.seconds ? p.uploadedAt.seconds * 1000 : new Date(p.uploadedAt || 0).getTime());
      if (uploadedAt > lastSeen) {
        newIds.add(p.id);
      }
    });
    
    if (newIds.size > 0) {
      setNewPhotoIds(newIds);
    }
  }, [savedPhotos, jobId]);

  // Update last seen timestamp on unmount
  useEffect(() => {
    return () => {
      if (jobId) {
        localStorage.setItem(`fieldsync_last_seen_${jobId}`, Date.now().toString());
      }
    };
  }, [jobId]);

  // Also persist new IDs to localStorage (for same-device session continuity)
  useEffect(() => {
    try {
      localStorage.setItem(`fieldsync_new_photos_${jobId}`, JSON.stringify([...newPhotoIds]));
    } catch {
      // ignore localStorage errors
    }
  }, [newPhotoIds, jobId]);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const clearNewBadge = (photoId) => {
    setNewPhotoIds(prev => {
      const next = new Set(prev);
      next.delete(photoId);
      return next;
    });
  };

  const openLightbox = (index) => {
    if (!savedPhotos?.length) return;
    const photo = savedPhotos[index];
    if (!photo) return;
    const url = typeof photo === 'string' ? photo : photo?.url;
    const name = typeof photo === 'string' ? '' : photo?.name || '';
    if (url) {
      setLightbox({ isOpen: true, index, url, name });
      setScale(1);
      setPan({ x: 0, y: 0 });
    }
  };

  const goToPhoto = (idx) => {
    if (!savedPhotos?.length) return;
    const len = savedPhotos.length;
    const safeIdx = ((idx % len) + len) % len;
    const photo = savedPhotos[safeIdx];
    const url = typeof photo === 'string' ? photo : photo?.url;
    const name = typeof photo === 'string' ? '' : photo?.name || '';
    if (url) {
      setLightbox({ isOpen: true, index: safeIdx, url, name });
      setScale(1);
      setPan({ x: 0, y: 0 });
    }
  };

  const nextPhoto = () => goToPhoto(lightbox.index + 1);
  const prevPhoto = () => goToPhoto(lightbox.index - 1);

  const closeLightbox = () => {
    setLightbox({ isOpen: false, index: 0, url: '', name: '' });
    setScale(1);
    setPan({ x: 0, y: 0 });
  };

  // Keyboard navigation
  useEffect(() => {
    if (!lightbox.isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') nextPhoto();
      if (e.key === 'ArrowLeft') prevPhoto();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox.isOpen, lightbox.index]);

  // Touch swipe
  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (diff > 50) nextPhoto();
    if (diff < -50) prevPhoto();
    setTouchStart(null);
  };

  const handleZoomIn = (e) => {
    e?.stopPropagation();
    setScale(s => Math.min(s + 0.5, 5));
  };

  const handleZoomOut = (e) => {
    e?.stopPropagation();
    setScale(s => {
      const newScale = Math.max(s - 0.5, 0.5);
      if (newScale === 1) setPan({ x: 0, y: 0 });
      return newScale;
    });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setScale(s => Math.min(s + 0.25, 5));
    } else {
      setScale(s => {
        const newScale = Math.max(s - 0.25, 0.5);
        if (newScale <= 1) setPan({ x: 0, y: 0 });
        return newScale;
      });
    }
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDownload = (e) => {
    e?.stopPropagation();
    if (lightbox.url) {
      const link = document.createElement('a');
      link.href = lightbox.url;
      link.download = lightbox.name || 'image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // ─── Download All Photos (client-side JSZip) ───
  const handleDownloadAll = async () => {
    if (!savedPhotos?.length) return;
    const urls = savedPhotos.filter(p => p.url).map(p => p.url);
    if (!urls.length) return;

    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    const folder = zip.folder('fieldsync-files');

    let completed = 0;
    for (const url of urls) {
      try {
        const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) throw new Error('Fetch failed');
        const blob = await response.blob();
        const urlObj = new URL(url);
        const fileName = urlObj.pathname.split('/').pop() || `file-${completed}.jpg`;
        folder.file(decodeURIComponent(fileName), blob);
        completed++;
      } catch (err) {
        console.warn('[DownloadAll] Failed to fetch:', url, err.message);
      }
    }

    if (completed === 0) {
      alert('No files could be downloaded. Please try again.');
      return;
    }

    const content = await zip.generateAsync({ type: 'blob' });
    const blobUrl = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = blobUrl;
    const formattedDate = jobDate ? new Date(jobDate).toLocaleDateString('en-GB').replace(/\//g, '-') : '';
    const safeTitle = (jobTitle || 'fieldsync-files').replace(/[^a-zA-Z0-9\s-]/g, '').trim();
    link.download = formattedDate ? `${safeTitle} - ${formattedDate}.zip` : `${safeTitle}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: URL.createObjectURL(file),
    }));
    setQueuedFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeQueued = (id) => {
    setQueuedFiles((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSavePhotos = async () => {
    if (queuedFiles.length === 0) return;
    setUploadingAll(true);
    const total = queuedFiles.length;
    const uploadedPhotos = [];
    const newlySavedIds = [];
    for (let i = 0; i < total; i++) {
      setUploadProgress(Math.round(((i) / total) * 100));
      const { photo, error } = await addPhoto(queuedFiles[i].file);
      if (photo) {
        uploadedPhotos.push(photo);
        newlySavedIds.push(photo.id);
      }
    }
    setUploadProgress(100);
    setTimeout(() => {
      setQueuedFiles([]);
      setUploadingAll(false);
      setUploadProgress(0);
      // Mark these as "new" for this session
      setNewPhotoIds(prev => {
        const next = new Set(prev);
        newlySavedIds.forEach(id => next.add(id));
        return next;
      });
      if (onPhotosSaved) onPhotosSaved(uploadedPhotos.map(p => ({ url: p.url, caption: '', uploadedAt: p.uploadedAt })));
    }, 500);
  };

  return (
    <div className="photo-upload">
      <div className="pu-header">
        <h4>Files & Photos ({savedPhotos.length + queuedFiles.length + (legacyPhotos?.length || 0)})</h4>
        {!isOnline && <span className="offline-badge">Offline</span>}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*, application/pdf"
        multiple
        onChange={handleFileChange}
        className="pu-file-input"
        id={`photo-input-${jobId}`}
      />
      <label htmlFor={`photo-input-${jobId}`} className="pu-camera-btn">
        📁 Upload Photo / Document
      </label>

      {/* ─── Queued / Unsaved Photos ─── */}
      {queuedFiles.length > 0 && (
        <div className="pu-queued-section">
          <p className="pu-queued-label">{queuedFiles.length} photo(s) ready to save</p>
          <div className="pu-grid">
            {queuedFiles.map((p) => (
              <div key={p.id} className={`pu-thumb pu-queued ${isPdf(p.file?.name) ? 'pu-pdf' : ''}`}
                onClick={() => {
                  if (isPdf(p.file?.name)) {
                    window.open(p.preview, '_blank');
                  } else {
                    openLightbox(p.preview);
                  }
                }}
              >
                {isPdf(p.file?.name) ? (
                  <div className="pu-pdf-preview">
                    <PdfIcon />
                    <span className="pu-pdf-name">{p.file.name}</span>
                  </div>
                ) : (
                  <img src={p.preview} alt="Preview" />
                )}
                <button className="pu-remove" onClick={(e) => { e.stopPropagation(); removeQueued(p.id); }} aria-label="Remove">×</button>
              <span className="pu-meta">Not saved</span>
              </div>
            ))}
          </div>
          <button
            className="btn-primary pu-save-btn"
            onClick={handleSavePhotos}
            disabled={uploadingAll}
          >
            {uploadingAll ? `Saving... ${uploadProgress}%` : '💾 Save Photos'}
          </button>
        </div>
      )}

      {uploadingAll && (
        <div className="pu-progress">
          <div className="pu-progress-bar" style={{ width: `${uploadProgress}%` }} />
        </div>
      )}

      {/* ─── Saved Photos ─── */}
      {savedPhotos.length > 0 && (
        <div className="pu-saved-section">
          <div className="pu-saved-header">
            <p className="pu-saved-label">Saved photos</p>
            <button
              className="pu-download-all-btn"
              onClick={handleDownloadAll}
              title="Download all files"
            >
              ⬇ Download All
            </button>
          </div>
          <div className="pu-photo-grid">
            {savedPhotos.map((p, i) => (
              <div key={p.id} className={`pu-thumb ${isPdf(p.name) ? 'pu-pdf' : ''} ${newPhotoIds.has(p.id) ? 'pu-new' : ''}`}
                onClick={() => {
                  clearNewBadge(p.id);
                  if (isPdf(p.name)) {
                    window.open(p.url, '_blank');
                  } else {
                    openLightbox(i);
                  }
                }}
              >
                {isPdf(p.name) ? (
                  <div className="pu-pdf-preview">
                    <PdfIcon />
                    <span className="pu-pdf-name">{p.name}</span>
                  </div>
                ) : (
                  <img src={p.url} alt={p.name} loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                )}
                {newPhotoIds.has(p.id) && <span className="pu-new-badge">New</span>}
                <span className="pu-meta">{p.uploaderName}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Legacy Photos (from job.jobPhotos) ─── */}
      {legacyPhotos?.length > 0 && (
        <div className="pu-saved-section">
          <div className="pu-saved-header">
            <p className="pu-saved-label">Job photos ({legacyPhotos.length})</p>
          </div>
          <div className="pu-photo-grid">
            {legacyPhotos.map((p, i) => {
              const url = typeof p === 'string' ? p : p?.url;
              const caption = typeof p === 'string' ? '' : p?.caption || '';
              if (!url) return null;
              return (
                <div key={`legacy-${i}`} className="pu-thumb"
                  onClick={() => {
                    if (isPdf(url)) {
                      window.open(url, '_blank');
                    } else {
                      // Open in a simple lightbox or new tab
                      window.open(url, '_blank');
                    }
                  }}
                >
                  <img src={url} alt={caption || `Job photo ${i + 1}`} loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                  {caption && <span className="pu-meta">{caption}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading && <p className="pu-loading">Loading photos...</p>}

      {/* ─── Lightbox ─── */}
      {lightbox.isOpen && (
        <div
          className="pu-lightbox-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeLightbox();
          }}
          onWheel={handleWheel}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="pu-lightbox-content">
            <div className="pu-lightbox-toolbar no-print">
              <button className="pu-lightbox-toolbtn" onClick={handleZoomOut} title="Zoom out">−</button>
              <span className="pu-lightbox-zoom">{Math.round(scale * 100)}%</span>
              <button className="pu-lightbox-toolbtn" onClick={handleZoomIn} title="Zoom in">+</button>
              <button className="pu-lightbox-toolbtn" onClick={handleDownload} title="Download">⬇</button>
              <button className="pu-lightbox-toolbtn pu-lightbox-close-btn" onClick={closeLightbox} title="Close">✕</button>
            </div>
            <div
              className="pu-lightbox-img-wrap"
              style={{
                cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
              }}
              onMouseDown={handleMouseDown}
            >
              {savedPhotos.length > 1 && (
                <button className="pu-lightbox-nav pu-lightbox-prev" onClick={prevPhoto}>‹</button>
              )}
              <img
                ref={lightboxImgRef}
                src={lightbox.url}
                alt={lightbox.name || 'Enlarged photo'}
                style={{
                  transform: `scale(${scale}) translate(${pan.x / scale}px, ${pan.y / scale}px)`,
                  transition: isDragging ? 'none' : 'transform 0.15s ease',
                }}
                draggable={false}
              />
              {savedPhotos.length > 1 && (
                <button className="pu-lightbox-nav pu-lightbox-next" onClick={nextPhoto}>›</button>
              )}
            </div>
            {lightbox.name && <p className="pu-lightbox-caption">{lightbox.name}</p>}
            {savedPhotos.length > 1 && (
              <p className="pu-lightbox-counter">{lightbox.index + 1} / {savedPhotos.length}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;